import {
  arrow,
  autoUpdate,
  flip,
  FloatingArrow,
  hide,
  offset,
  Placement,
  shift,
  useDismiss,
  useFloating,
  useInteractions,
  useRole
} from '@floating-ui/react'
import {
  Box,
  Button,
  IconButton,
  Paper,
  Portal,
  Stack,
  styled,
  SvgIcon,
  Typography
} from '@mui/material'
import { X } from '@phosphor-icons/react'
import { Observables } from '@reactour/utils'
import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { useSizes } from './hooks'
import { Mask } from './Mask'

export type Step = {
  hidden?: boolean
  target: string
  heading?: string | React.ReactElement
  content?: string | React.ReactElement
  placement?: Placement
  offset?: number
  closeButton?: boolean
  nextButton?: boolean
  backButton?: boolean
}

type Props = {
  highContrast?: boolean
  disablePortal?: boolean
  progress?: boolean
  open?: boolean
  index?: number
  steps?: Step[]
  offset?: number
  placement?: Placement
  overlay?: boolean
  overlayClose?: boolean
  closeButton?: boolean
  nextButton?: boolean
  backButton?: boolean
  stepDisplay?: boolean
  controllButtons?: boolean
  onClose?: () => void
  onDone?: () => void
}

const Arrow = styled(FloatingArrow)({})

export function Onboard(props: Props) {
  const {
    highContrast = false,
    disablePortal = false,
    progress = false,
    open: openProp,
    index: indexProp,
    steps: stepsProp = [],
    offset: offsetProp = 16,
    placement = 'bottom',
    overlay = false,
    overlayClose = false,
    stepDisplay: stepDisplayProp = false,
    controllButtons = false,
    closeButton: closeButtonProp = false,
    nextButton: nextButtonProp = false,
    backButton: backButtonProp = false,
    onClose,
    onDone
  } = props

  const [index, setIndex] = useState(indexProp || 0)
  useEffect(() => {
    setIndex(indexProp || 0)
  }, [indexProp])

  const steps = useMemo(
    () => stepsProp.filter((step) => !step.hidden),
    [stepsProp]
  )

  const {
    target,
    heading,
    content,
    offset: offsetStep,
    placement: stepPlacement,
    closeButton: closeButtonStep,
    nextButton: nextButtonStep,
    backButton: backButtonStep
  } = steps?.[index] || {}

  const arrowRef = useRef(null)
  const [open, setOpen] = useState(openProp)

  const close = () => {
    setOpen(false)
    onClose?.()
  }

  const { refs, floatingStyles, context, middlewareData } = useFloating({
    placement: stepPlacement || placement,
    open: open,
    onOpenChange: () => {
      if (!overlayClose) return
      close()
    },
    middleware: [
      shift({
        padding: 8
      }),
      flip(),
      offset(offsetStep || offsetProp),
      arrow({
        element: arrowRef
      }),
      hide()
    ],
    whileElementsMounted: autoUpdate
  })

  const role = useRole(context, {
    role: 'tooltip'
  })

  const dismiss = useDismiss(context)

  // Merge all the interactions into prop getters
  const { getFloatingProps } = useInteractions([role, dismiss])

  const referenceHidden = middlewareData.hide?.referenceHidden

  const closeButton = closeButtonStep || closeButtonProp || false

  const nextButton =
    controllButtons || nextButtonStep || nextButtonProp || false

  const backButton =
    controllButtons || backButtonStep || backButtonProp || false

  const lastStep = index === steps.length - 1

  const { sizes, transition, observableRefresher } = useSizes({
    selector: target
  })

  useLayoutEffect(() => {
    setOpen(openProp)
  }, [openProp])

  useLayoutEffect(() => {
    if (!target) return
    const element = document.querySelector(target) || null
    if (element) refs.setReference(element)
    const shouldOpen = openProp === false ? false : Boolean(element)
    setOpen(shouldOpen)
  }, [target, index])

  if (!steps[index]) return null

  return (
    <>
      <Portal disablePortal={disablePortal}>
        <Observables refresh={observableRefresher} />
        {open && overlay ? (
          <Mask
            padding={8}
            sizes={transition ? initialSizes : sizes}
            styles={{
              maskWrapper: (rest) => ({
                ...rest,
                zIndex: 1300,
                opacity: 0.15
              }),
              maskArea: (rest) =>
                ({
                  ...rest,
                  rx: '8px'
                } as any)
            }}
            onClick={overlayClose ? close : undefined}
          />
        ) : null}

        {open ? (
          <Paper
            variant='outlined'
            ref={refs.setFloating}
            sx={[
              referenceHidden ? { display: 'none' } : {},
              {
                position: 'relative',
                maxWidth: 300,
                px: 1.5,
                py: 1,
                zIndex: (theme) => theme.zIndex.tooltip,
                boxShadow: '0px 1px 18px rgba(0, 0, 0, 0.1)',
                ...floatingStyles
              },
              highContrast
                ? {
                    backgroundColor: '#FDE047',
                    borderColor: '#000000',
                    borderWidth: 1.5
                  }
                : {}
            ]}
            {...getFloatingProps()}
          >
            {closeButton ? (
              <IconButton
                sx={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  mt: 1,
                  mr: 1,
                  width: 22,
                  height: 22
                }}
                onClick={close}
              >
                <SvgIcon
                  component={X}
                  inheritViewBox
                  sx={{
                    width: 16,
                    height: 16
                  }}
                />
              </IconButton>
            ) : null}

            <Stack
              direction='column'
              gap={1}
            >
              {stepDisplayProp ? (
                <>
                  {typeof progress === 'boolean' ? (
                    <>
                      <Typography
                        variant='next:caption'
                        fontWeight={400}
                      >
                        Step {index + 1} of {steps.length}
                      </Typography>
                    </>
                  ) : null}
                </>
              ) : null}

              {typeof heading === 'string' ? (
                <Typography
                  variant='next:subtitle1'
                  letterSpacing='tight'
                >
                  {heading}
                </Typography>
              ) : null}

              {typeof heading !== 'string' && React.isValidElement(heading) ? (
                <Box
                  component={Typography}
                  variant='next:subtitle1'
                  letterSpacing='tight'
                >
                  {heading}
                </Box>
              ) : null}

              {typeof content === 'string' ? (
                <Typography
                  variant='next:subtitle2'
                  fontWeight={400}
                >
                  {content}
                </Typography>
              ) : null}

              {typeof content !== 'string' && React.isValidElement(content) ? (
                <Box
                  component={Typography}
                  variant='next:subtitle2'
                  fontWeight={400}
                >
                  {content}
                </Box>
              ) : null}

              {controllButtons || backButton || nextButton ? (
                <Stack
                  direction='row'
                  justifyContent='flex-end'
                  gap={1}
                >
                  {backButton ? (
                    <>
                      {index > 0 ? (
                        <Button
                          size='small'
                          variant='text'
                          color='secondary'
                          sx={{
                            borderRadius: 0.9
                          }}
                          onClick={() => {
                            setIndex((previous) => previous - 1)
                          }}
                        >
                          Back
                        </Button>
                      ) : null}
                    </>
                  ) : null}

                  {nextButton ? (
                    <Button
                      size='small'
                      color='secondary'
                      sx={{
                        borderRadius: 0.9
                      }}
                      onClick={() => {
                        if (lastStep) {
                          onDone?.()
                          close()
                          return
                        }
                        setIndex((previous) => previous + 1)
                      }}
                    >
                      {lastStep ? 'Done' : 'Next'}
                    </Button>
                  ) : null}
                </Stack>
              ) : null}
            </Stack>
            <Arrow
              ref={arrowRef}
              context={context}
              width={9}
              height={6}
              {...(highContrast
                ? {
                    stroke: '#000',
                    fill: '#FDE047',
                    strokeWidth: 1.5
                  }
                : {
                    fill: '#fff'
                  })}
            />
          </Paper>
        ) : null}
      </Portal>
    </>
  )
}

const initialSizes = {
  bottom: 0,
  height: 0,
  left: 0,
  right: 0,
  top: 0,
  width: 0,
  x: 0,
  y: 0
}
